import React, { useState, useEffect } from 'react';
import OnboardingScreen from './features/onboarding/OnboardingScreen';
import ChildProfileScreen from './features/onboarding/ChildProfileScreen';
import HomeScreen from './features/home/HomeScreen';
import ChatScreen from './features/chat/ChatScreen';
import ChatListScreen from './features/chat/ChatListScreen';
import PlayScreen from './features/play/PlayScreen';
import DiaryScreen from './features/diary/DiaryScreen';
import ProfileScreen from './features/profile/ProfileScreen';
import LoginScreen from './features/auth/LoginScreen';
import ResetPasswordScreen from './features/auth/ResetPasswordScreen';
import SettingsScreen from './features/settings/SettingsScreen';
import { Screen } from './shared/types/index';
import { Button } from './shared/components/index';
import { useAuth } from './shared/hooks/index';
import { userService } from './shared/services/index';
import VersionDisplay from './shared/components/VersionDisplay';
import './styles/index.css';

const App: React.FC = () => {
  const { user, loading, logout } = useAuth();
  const [currentScreen, setCurrentScreen] = useState<Screen>('onboarding');
  const [selectedEmotion, setSelectedEmotion] = useState('');
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [childProfileCompleted, setChildProfileCompleted] = useState(() => {
    return localStorage.getItem('calmi_child_profile_completed') === 'true';
  });

  useEffect(() => {
    // Al iniciar la app siempre se vuelve al modo niño
    localStorage.setItem('calmi_profile_mode', 'child');

    // Verificar si la URL contiene el modo de reset de contraseña
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    if (mode === 'resetPassword') {
      setIsResetPassword(true);
    }
  }, []);

  const handleNavigate = (screen: string) => {
    setCurrentScreen(screen as Screen);
  };

  const handleOnboardingComplete = async (emotion: string) => {
    setSelectedEmotion(emotion);
    if (user) {
      await userService.completeOnboarding(user.uid, emotion);
    }
    setCurrentScreen('home');
  };

  const handleChildProfileComplete = () => {
    setChildProfileCompleted(true);
    setCurrentScreen('home');
  };

  // Mostrar pantalla de reset de contraseña si está en modo reset
  if (isResetPassword) {
    return <ResetPasswordScreen />;
  }

  // Mostrar pantalla de carga mientras se verifica autenticación
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Mostrar pantalla de login si no hay usuario autenticado
  if (!user) {
    return <LoginScreen />;
  }

  // Determinar la pantalla inicial basada en si el usuario completó el onboarding
  const initialScreen = user.onboardingCompleted ? 'home' : 'onboarding';
  const displayScreen = currentScreen === 'onboarding' ? initialScreen : currentScreen;

  // Mostrar pantalla de perfil del menor si no está completado
  if (!childProfileCompleted && displayScreen === 'home') {
    return <ChildProfileScreen onComplete={handleChildProfileComplete} />;
  }

  // Obtener nombre del menor para mostrar en el saludo
  const getChildName = () => {
    const childProfile = localStorage.getItem('calmi_child_profile');
    if (childProfile) {
      const parsed = JSON.parse(childProfile);
      return parsed.name || user.displayName || user.email?.split('@')[0] || 'Usuario';
    }
    return user.displayName || user.email?.split('@')[0] || 'Usuario';
  };

  const childName = getChildName();
  const userName = user.displayName || user.email?.split('@')[0] || 'Usuario';

  return (
    <div className="App">
      {displayScreen === 'onboarding' && (
        <OnboardingScreen onNext={handleOnboardingComplete} />
      )}
      {displayScreen === 'home' && (
        <HomeScreen userName={childName} onNavigate={handleNavigate} />
      )}
      {displayScreen === 'chat' && (
        <ChatScreen userName={userName} onNavigate={handleNavigate} />
      )}
      {displayScreen === 'chat-list' && (
        <ChatListScreen userName={userName} onNavigate={handleNavigate} />
      )}
      {displayScreen === 'play' && (
        <PlayScreen userName={userName} onNavigate={handleNavigate} />
      )}
      {displayScreen === 'diary' && (
        <DiaryScreen userName={userName} onNavigate={handleNavigate} />
      )}
      {displayScreen === 'profile' && (
        <ProfileScreen userName={userName} onNavigate={handleNavigate} />
      )}
      {displayScreen === 'settings' && (
        <SettingsScreen onNavigate={handleNavigate} />
      )}
      <VersionDisplay />
    </div>
  );
};

export default App;
